import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import {
  X, Download, Linkedin, Share2, MessageCircle, Sparkles, TrendingUp,
  Trophy, Calendar,
} from "lucide-react";
import { CFR_FULL, CFR_SHORT } from "../../lib/brand";
import { PUBLIC_APP_URL } from "../../lib/api";

/** Sharable certificate shown after a successful AI re-run with score improvement.
 *  Props:
 *    open: boolean
 *    onClose: () => void
 *    company: { name, country, sector }
 *    previousScore: number
 *    newScore: number
 */
export default function CertificateModal({ open, onClose, company, previousScore, newScore }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Set a verifyId on first open so the share URL is stable across share buttons
  const [verifyId] = useState(
    () => `cfr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const delta = newScore - previousScore;
  const today = new Date().toLocaleDateString(undefined, {
    month: "long", day: "numeric", year: "numeric",
  });
  const shareUrl = `${PUBLIC_APP_URL}/cert/${verifyId}`;
  const shareText = encodeURIComponent(
    `🎉 ${company?.name || "Our team"} just improved our ${CFR_SHORT} from ${previousScore} → ${newScore} ` +
    `(+${delta} points) on Clinq — connecting African SMEs with institutional funders. ` +
    `#FunderReadiness #AfricaInvests`);
  const linkedinUrl =
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`;

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2, backgroundColor: "#0E2E2C",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(company?.name || "clinq-cfr")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase()}-cfr-certificate.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setBusy(false); }
  };

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${CFR_SHORT} improvement`,
          text: decodeURIComponent(shareText),
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    await navigator.clipboard.writeText(`${decodeURIComponent(shareText)} ${shareUrl}`);
    window.alert("Share text copied to clipboard!");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         data-testid="cfr-certificate-modal">
      <div className="w-full max-w-2xl">
        {/* Certificate card (the part that gets downloaded as PNG) */}
        <div
          ref={cardRef}
          className="relative overflow-hidden rounded-3xl"
          style={{
            background: "linear-gradient(135deg,#0E2E2C 0%,#0A4543 45%,#0A8B85 100%)",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          {/* Decorative grain */}
          <div className="pointer-events-none absolute inset-0 opacity-20"
               style={{
                 background:
                   "radial-gradient(circle at 20% 10%, rgba(247,176,11,0.35), transparent 40%)," +
                   "radial-gradient(circle at 80% 90%, rgba(123,211,206,0.35), transparent 40%)",
               }}/>
          <div className="relative px-8 py-10 sm:px-12 sm:py-14">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-[#7BD3CE]">
                <Sparkles className="h-3.5 w-3.5"/>
                CLINQ · FUNDER READINESS
              </div>
              <div className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-bold tracking-widest text-amber-300">
                <Trophy className="h-3 w-3"/> CERTIFICATE
              </div>
            </div>

            <h2 className="mt-7 text-3xl font-extrabold leading-tight text-white sm:text-4xl">
              {company?.name || "Your business"}
            </h2>
            <p className="mt-1 text-sm text-white/70">
              has improved its {CFR_FULL}
            </p>

            {/* Score delta */}
            <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Before</p>
                <p className="text-5xl font-extrabold text-white/40 line-through">{previousScore}</p>
              </div>
              <TrendingUp className="mb-1 h-8 w-8 text-emerald-300"/>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#7BD3CE]">After</p>
                <p className="text-6xl font-extrabold text-white">{newScore}<span className="text-2xl text-white/60">/100</span></p>
              </div>
              <div className="ml-auto rounded-2xl bg-emerald-500/15 px-4 py-3 text-right">
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-200">Improvement</p>
                <p className="text-3xl font-extrabold text-emerald-300">+{delta}</p>
                <p className="text-[10px] text-emerald-200/70">points</p>
              </div>
            </div>

            {/* Footer band */}
            <div className="mt-10 flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-5 text-xs text-white/60">
              <div>
                <p className="font-bold text-white/80">{company?.country || "Africa"} · {company?.sector || ""}</p>
                <p className="mt-1 inline-flex items-center gap-1.5">
                  <Calendar className="h-3 w-3"/> {today}
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold tracking-wider text-white/80">CLINQ.AFRICA</p>
                <p className="mt-0.5">Verify: <span className="font-mono">{verifyId}</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-4 rounded-2xl bg-white p-4 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-neutral-900">Share your achievement</p>
              <p className="text-xs text-neutral-500">
                Tell your network — Clinq helps investors find businesses like yours.
              </p>
            </div>
            <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100"
                    data-testid="cfr-cert-close">
              <X className="h-5 w-5"/>
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <a
              href={linkedinUrl} target="_blank" rel="noreferrer"
              data-testid="cfr-cert-share-linkedin"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A66C2] px-3 py-2 text-xs font-bold text-white hover:bg-[#084d8f]">
              <Linkedin className="h-3.5 w-3.5"/> LinkedIn
            </a>
            <a
              href={whatsappUrl} target="_blank" rel="noreferrer"
              data-testid="cfr-cert-share-whatsapp"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-xs font-bold text-white hover:bg-[#1da851]">
              <MessageCircle className="h-3.5 w-3.5"/> WhatsApp
            </a>
            <button
              onClick={tryNativeShare}
              data-testid="cfr-cert-share-native"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-2 text-xs font-bold text-white hover:bg-neutral-800">
              <Share2 className="h-3.5 w-3.5"/> More…
            </button>
            <button
              onClick={downloadPng}
              disabled={busy}
              data-testid="cfr-cert-download"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50">
              <Download className="h-3.5 w-3.5"/> {busy ? "..." : "Download PNG"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
